import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ComparisonEvent,
  LprComparison,
  fiveSecondEvent,
} from "./loop-comparison";

export type ExceptionPackage = {
  id: string;
  title: string;
  comparison: LprComparison;
  events: { id: string; source_seconds: number }[];
};
type PassageException = {
  packageId: string;
  title: string;
  event: ComparisonEvent;
  sourceSeconds?: number;
};
type ReadException = {
  packageId: string;
  title: string;
  read: LprComparison["observations"][number];
  nearby: (PassageException & { delta: number })[];
};

export function buildExceptionQueues(packages: ExceptionPackage[]) {
  const passages: PassageException[] = [];
  const unclear: PassageException[] = [];
  const pairedCompetition: PassageException[] = [];
  const reads: ReadException[] = [];
  for (const pkg of packages) {
    const events = pkg.comparison.events.map(fiveSecondEvent);
    const nonblank = new Set(
      pkg.comparison.observations
        .filter((read) => read.plate_number?.trim())
        .map((read) => read.id),
    );
    const readLinks = new Map<string, number>();
    for (const event of events)
      for (const candidate of event.candidates) {
        if (nonblank.has(candidate.observation_id))
          readLinks.set(
            candidate.observation_id,
            (readLinks.get(candidate.observation_id) || 0) + 1,
          );
      }
    const assigned = new Set(
      events
        .filter((event) => event.status === "time_candidate")
        .map((event) => event.observation_id),
    );
    const passage = (event: ComparisonEvent): PassageException => ({
      packageId: pkg.id,
      title: pkg.title,
      event,
      sourceSeconds: pkg.events.find((item) => item.id === event.event_id)
        ?.source_seconds,
    });
    for (const event of events) {
      if (event.status === "no_associated_read") passages.push(passage(event));
      if (event.status === "ambiguous") unclear.push(passage(event));
      if (
        event.status === "time_candidate" &&
        (event.candidates.filter((candidate) =>
          nonblank.has(candidate.observation_id),
        ).length > 1 ||
          (readLinks.get(event.observation_id || "") || 0) > 1)
      )
        pairedCompetition.push(passage(event));
    }
    for (const read of pkg.comparison.observations) {
      if (!read.plate_number?.trim() || assigned.has(read.id)) continue;
      const nearby = events.flatMap((event) => {
        const candidate = event.candidates.find(
          (item) => item.observation_id === read.id,
        );
        return candidate
          ? [{ ...passage(event), delta: candidate.signed_delta_seconds }]
          : [];
      });
      reads.push({ packageId: pkg.id, title: pkg.title, read, nearby });
    }
  }
  return { passages, reads, unclear, pairedCompetition };
}

function elapsed(seconds?: number) {
  if (seconds === undefined || !Number.isFinite(seconds))
    return "Time unavailable";
  return `${Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0")}:${Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0")} elapsed`;
}
const eventUrl = (row: PassageException) =>
  `/loop/comparison?${new URLSearchParams({ package: row.packageId, event: row.event.event_id })}#loop-crossing-evidence`;

export function ComparisonExceptions({
  packages,
  onLink,
  selectedReadId,
  selectedPackageId,
}: {
  packages: ExceptionPackage[];
  onLink: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  selectedReadId?: string | null;
  selectedPackageId?: string | null;
}) {
  const queues = buildExceptionQueues(packages);
  const [category, setCategory] = useState<"passages" | "reads" | "unclear">(
    selectedReadId ? "reads" : "passages",
  );
  useEffect(() => {
    if (selectedReadId) setCategory("reads");
  }, [selectedReadId, selectedPackageId]);
  const selectedReadExists = queues.reads.some(
    (row) =>
      row.read.id === selectedReadId && row.packageId === selectedPackageId,
  );
  const tabs = [
    {
      id: "passages" as const,
      label: "Unpaired passages",
      count: queues.passages.length,
    },
    {
      id: "reads" as const,
      label: "Unused plate reads",
      count: queues.reads.length,
    },
    {
      id: "unclear" as const,
      label: "Association review",
      count: queues.unclear.length + queues.pairedCompetition.length,
    },
  ];
  const passageRows =
    category === "unclear"
      ? [...queues.unclear, ...queues.pairedCompetition]
      : queues.passages;
  if (!packages.length)
    return (
      <section
        id="loop-comparison-exceptions"
        className="loop-exceptions"
        aria-label="Comparison exceptions"
      >
        <h2>No LPR comparisons available</h2>
        <p className="loop-exceptions-intro">
          Exception counts will appear when comparison data is available. This
          does not mean there are no unpaired passages or reads.
        </p>
      </section>
    );
  return (
    <section
      id="loop-comparison-exceptions"
      className="loop-exceptions"
      aria-label="Comparison exceptions"
    >
      <header>
        <div>
          <p className="loop-eyebrow">ACROSS COMPARED RECORDINGS</p>
          <h2>Exceptions to inspect</h2>
        </div>
        <span className="loop-chip">Within 5 seconds</span>
      </header>
      <p className="loop-exceptions-intro">
        Start with the passages and reads that do not pair cleanly. These are
        review leads, not confirmed missed vehicles or reader failures.
      </p>
      <div
        className="loop-exception-tabs"
        role="group"
        aria-label="Exception categories"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            aria-pressed={category === tab.id}
            onClick={() => setCategory(tab.id)}
          >
            {tab.label}
            <span>{tab.count}</span>
          </button>
        ))}
      </div>
      <div className="loop-exception-results" aria-live="polite">
        <h3>{tabs.find((tab) => tab.id === category)?.label}</h3>
        <p className="loop-exception-explanation">
          {category === "passages"
            ? "A passage was counted, but no plate read is paired within five seconds. Open its clip to inspect what happened."
            : category === "reads"
              ? "These export rows contain plate text but are not used by a paired passage. Repeat reads and unclear associations can appear here; each row is not necessarily a separate vehicle."
              : "Nearby vehicles and reads have competing timing matches. These passages are separate from the unpaired passage queue; their unused reads may also appear in the read queue."}
        </p>
        {category === "reads" ? (
          <>
            <p className="loop-exception-explanation">
              {queues.reads.filter((row) => row.nearby.length > 0).length} have
              possible passages;{" "}
              {queues.reads.filter((row) => row.nearby.length === 0).length}{" "}
              have none within five seconds. Passage and read exceptions can
              describe the same unresolved association; do not add them as
              missed vehicles.
            </p>
            {selectedReadId && !selectedReadExists && (
              <p className="loop-exception-empty">
                The requested read is not in the current unused-read queue.
              </p>
            )}
            {!queues.reads.length && (
              <p className="loop-exception-empty">
                No unused nonblank plate reads in these comparisons.
              </p>
            )}
            {queues.reads.map((row) => (
              <article
                className="loop-exception-row"
                key={`${row.packageId}:${row.read.id}`}
                aria-current={
                  selectedReadId === row.read.id &&
                  selectedPackageId === row.packageId
                    ? "true"
                    : undefined
                }
              >
                <div className="loop-exception-row-heading">
                  <div>
                    <p>{row.title}</p>
                    <h4>
                      <Link
                        to={`/loop/comparison?${new URLSearchParams({ package: row.packageId, read: row.read.id })}#loop-comparison-exceptions`}
                        onClick={onLink}
                      >
                        {row.read.plate_number}
                      </Link>
                    </h4>
                    <time>
                      {row.read.observed_at.replace("T", " ")} · export time
                    </time>
                  </div>
                  <span className="loop-chip">Not paired</span>
                </div>
                {row.nearby.length ? (
                  <div className="loop-exception-nearby">
                    <strong>
                      {row.nearby.length > 1 ||
                      row.nearby.some(
                        (item) => item.event.status === "ambiguous",
                      )
                        ? "Competing crossing evidence"
                        : "Nearby crossing evidence"}{" "}
                      · read remains unassigned
                    </strong>
                    {row.nearby.map((item) => (
                      <div key={item.event.event_id}>
                        <Link to={eventUrl(item)} onClick={onLink}>
                          Open crossing clip · {elapsed(item.sourceSeconds)}
                        </Link>
                        <span>
                          {Math.abs(item.delta).toFixed(1)} sec{" "}
                          {item.delta < 0 ? "before" : "after"} passage
                          {item.event.status === "time_candidate"
                            ? " · passage paired with a different read"
                            : " · no pairing confirmed"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>
                    No crossing within five seconds is linked to this read in
                    the available comparison. No vehicle clip is assigned.
                  </p>
                )}
              </article>
            ))}
          </>
        ) : (
          <>
            {category === "unclear" && (
              <p className="loop-exception-explanation">
                {queues.unclear.length} unresolved associations ·{" "}
                {queues.pairedCompetition.length} provisionally paired passages
                with competing evidence. Existing pairs remain provisional;
                these are not additional unpaired passages.
              </p>
            )}
            {!passageRows.length && (
              <p className="loop-exception-empty">
                {category === "unclear"
                  ? "No unclear associations in these comparisons."
                  : "No unpaired passages in these comparisons."}
              </p>
            )}
            {passageRows.map((row) => (
              <article
                className="loop-exception-row"
                key={`${row.packageId}:${row.event.event_id}`}
              >
                <div className="loop-exception-row-heading">
                  <div>
                    <p>{row.title}</p>
                    <h4>
                      {row.event.evidence_annotation?.label ||
                        (category === "unclear"
                          ? row.event.status === "time_candidate"
                            ? "Provisionally paired · competing evidence"
                            : "Unresolved association"
                          : "Passage without a paired read")}
                    </h4>
                    <span>{elapsed(row.sourceSeconds)}</span>
                  </div>
                  <Link to={eventUrl(row)} onClick={onLink}>
                    Open passage clip →
                  </Link>
                </div>
                {row.event.evidence_annotation ? (
                  <p>{row.event.evidence_annotation.note}</p>
                ) : row.event.reason ? (
                  <p>{row.event.reason}</p>
                ) : null}
                {row.event.candidates.length > 0 && (
                  <p>
                    {row.event.candidates.length} nearby{" "}
                    {row.event.candidates.length === 1 ? "read" : "reads"}{" "}
                    within five seconds · association needs review.
                  </p>
                )}
              </article>
            ))}
          </>
        )}
      </div>
    </section>
  );
}
